/**
 * Custom hook for workout routine functionality
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  createWorkoutRoutine,
  updateWorkoutRoutine,
  fetchWorkoutRoutine,
  fetchFavoriteRoutines,
  setFavoriteRoutines,
  uploadCoverPhoto,
  setUserPublicRoutines,
  setUserPrivateRoutines,
  setAllPublicRoutines,
  updateRoutineData,
  clearCurrentRoutine,
  clearError,
  setLocalFavorite
} from '../store/slices/workoutRoutineSlice';
import { 
  subscribeToOwnRoutines, 
  subscribeToAllPublicRoutines, 
  subscribeToWorkoutRoutine,
  subscribeToFavoriteRoutines
} from '../services/firebase/workout-routine.service';
import {
  WorkoutRoutineBase,
  WorkoutRoutine,
  WorkoutRoutineSearchParams
} from '../types/workout-routine';

export const useWorkoutRoutine = () => {
  const dispatch = useAppDispatch();
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  
  // Subscription cleanup references
  const ownPublicRoutinesUnsubRef = useRef<(() => void) | null>(null);
  const ownPrivateRoutinesUnsubRef = useRef<(() => void) | null>(null);
  const allPublicRoutinesUnsubRef = useRef<(() => void) | null>(null);
  const currentRoutineUnsubRef = useRef<(() => void) | null>(null);
  const favoriteRoutinesUnsubRef = useRef<(() => void) | null>(null);
  
  // Get the current user from the auth state
  const { user } = useAppSelector(state => state.auth);
  
  // Get workout routine state from Redux
  const {
    routines,
    userPublicRoutines,
    userPrivateRoutines,
    allPublicRoutines,
    favoriteRoutines,
    wereFavRoutinesFetched,
    currentRoutine,
    availableTags,
    isLoading: storeLoading,
    error: storeError
  } = useAppSelector(state => state.workoutRoutine);
  
  // Clean up subscriptions on unmount
  useEffect(() => {
    return () => {
      if (ownPublicRoutinesUnsubRef.current) ownPublicRoutinesUnsubRef.current();
      if (ownPrivateRoutinesUnsubRef.current) ownPrivateRoutinesUnsubRef.current();
      if (allPublicRoutinesUnsubRef.current) allPublicRoutinesUnsubRef.current();
      if (currentRoutineUnsubRef.current) currentRoutineUnsubRef.current();
      if (favoriteRoutinesUnsubRef.current) favoriteRoutinesUnsubRef.current();
    };
  }, []);
  
  /**
   * Subscribe to the user's own public routines
   */
  const subscribeToUserPublicRoutines = useCallback((userId: string) => {
    // Clean up existing subscription if any
    if (ownPublicRoutinesUnsubRef.current) {
      ownPublicRoutinesUnsubRef.current();
      ownPublicRoutinesUnsubRef.current = null;
    }
    
    // Set up new subscription
    ownPublicRoutinesUnsubRef.current = subscribeToOwnRoutines(
      userId,
      'public',
      (routines) => {
        dispatch(setUserPublicRoutines({ routines }));
      }
    );
  }, [dispatch]);
  
  /**
   * Subscribe to the user's own private routines
   */
  const subscribeToUserPrivateRoutines = useCallback((userId: string) => {
    // Clean up existing subscription if any
    if (ownPrivateRoutinesUnsubRef.current) {
      ownPrivateRoutinesUnsubRef.current();
      ownPrivateRoutinesUnsubRef.current = null;
    }
    
    // Set up new subscription
    ownPrivateRoutinesUnsubRef.current = subscribeToOwnRoutines(
      userId,
      'private',
      (routines) => {
        dispatch(setUserPrivateRoutines({ routines }));
      }
    );
  }, [dispatch]);
  
  /**
   * Subscribe to all public routines
   */
  const subscribeToPublicRoutines = useCallback(() => {
    // Clean up existing subscription if any
    if (allPublicRoutinesUnsubRef.current) {
      allPublicRoutinesUnsubRef.current();
      allPublicRoutinesUnsubRef.current = null;
    }
    
    // Set up new subscription
    allPublicRoutinesUnsubRef.current = subscribeToAllPublicRoutines(
      (routines) => {
        dispatch(setAllPublicRoutines({ routines }));
      }
    );
  }, [dispatch]);
  
  /**
   * Subscribe to a specific routine
   */
  const subscribeToRoutine = useCallback((routineKey: string) => {
    // Clean up existing subscription if any
    if (currentRoutineUnsubRef.current) {
      currentRoutineUnsubRef.current();
      currentRoutineUnsubRef.current = null;
    }
    
    // Set up new subscription
    currentRoutineUnsubRef.current = subscribeToWorkoutRoutine(
      routineKey,
      (routine) => {
        dispatch(updateRoutineData(routine));
      }
    );
    
    // Return cleanup function
    return () => {
      if (currentRoutineUnsubRef.current) {
        currentRoutineUnsubRef.current();
        currentRoutineUnsubRef.current = null;
      }
    };
  }, [dispatch]);
  
  /**
   * Subscribe to user's favorite routines
   */
  const subscribeToUserFavorites = useCallback((userId: string) => {
    // Clean up existing subscription if any
    if (favoriteRoutinesUnsubRef.current) {
      favoriteRoutinesUnsubRef.current();
      favoriteRoutinesUnsubRef.current = null;
    }
    
    // Set up new subscription
    favoriteRoutinesUnsubRef.current = subscribeToFavoriteRoutines(
      userId,
      (favoriteRoutines) => {
        // This doesn't directly update Redux store, we'll use the async thunk for that
        if (!wereFavRoutinesFetched) {
          dispatch(fetchFavoriteRoutines(userId));
        }
      }
    );
  }, [dispatch, wereFavRoutinesFetched]);
  
  /**
   * Fetch a specific routine by ID
   */
  const getRoutineById = useCallback(async (routineKey: string) => {
    try {
      setLocalLoading(true);
      await dispatch(fetchWorkoutRoutine(routineKey)).unwrap();
      setLocalLoading(false);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to fetch workout routine');
      setLocalLoading(false);
    }
  }, [dispatch]);
  
  /**
   * Create a new workout routine
   */
  const createNewWorkoutRoutine = useCallback(async (routineData: WorkoutRoutineBase) => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return null;
    }
    
    try {
      setLocalLoading(true);
      const routineKey = await dispatch(createWorkoutRoutine({
        userId: user.uid,
        routineData
      })).unwrap();
      setLocalLoading(false);
      return routineKey;
    } catch (error: any) {
      setLocalError(error.message || 'Failed to create workout routine');
      setLocalLoading(false);
      return null;
    }
  }, [dispatch, user?.uid]);
  
  /**
   * Update an existing workout routine
   */
  const updateExistingWorkoutRoutine = useCallback(async (
    routineKey: string,
    updatedData: Partial<WorkoutRoutineBase>
  ) => {
    try {
      setLocalLoading(true);
      await dispatch(updateWorkoutRoutine({
        routineKey,
        updatedData
      })).unwrap();
      setLocalLoading(false);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to update workout routine');
      setLocalLoading(false);
    }
  }, [dispatch]);
  
  // Like functionality removed - use the unified useLikes hook instead
  
  /**
   * Toggle favorite status for a routine
   */
  const toggleFavorite = useCallback(async (
    routineKey: string,
    author: string,
    isFavorite: boolean
  ) => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return;
    }
    
    // Optimistic update
    dispatch(setLocalFavorite({
      routineKey,
      author,
      isFavorite
    }));
    
    try {
      const updatedFavorites = [...favoriteRoutines];
      
      if (isFavorite) {
        if (!updatedFavorites.some(r => r.routineKey === routineKey)) {
          updatedFavorites.push({ routineKey, author });
        }
      } else {
        const index = updatedFavorites.findIndex(r => r.routineKey === routineKey);
        if (index !== -1) {
          updatedFavorites.splice(index, 1);
        }
      }
      
      await dispatch(setFavoriteRoutines({
        userId: user.uid,
        favoriteRoutines: updatedFavorites
      })).unwrap();
    } catch (error: any) {
      // Revert optimistic update on failure
      dispatch(setLocalFavorite({
        routineKey,
        author,
        isFavorite: !isFavorite
      }));
      setLocalError(error.message || 'Failed to update favorite status');
    }
  }, [dispatch, user?.uid, favoriteRoutines]);
  
  /**
   * Upload a cover photo for a routine
   */
  const uploadRoutineCoverPhoto = useCallback(async (
    routineKey: string,
    photoUri: string,
    aspectRatio?: number
  ) => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return null;
    }
    
    try {
      setLocalLoading(true);
      const downloadURL = await dispatch(uploadCoverPhoto({
        userId: user.uid,
        routineKey,
        photoUri,
        aspectRatio
      })).unwrap();
      setLocalLoading(false);
      return downloadURL;
    } catch (error: any) {
      setLocalError(error.message || 'Failed to upload cover photo');
      setLocalLoading(false);
      return null;
    }
  }, [dispatch, user?.uid]);
  
  /**
   * Fetch user's favorite routines
   */
  const getUserFavorites = useCallback(async () => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return;
    }
    
    try {
      setLocalLoading(true);
      await dispatch(fetchFavoriteRoutines(user.uid)).unwrap();
      setLocalLoading(false);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to fetch favorite routines');
      setLocalLoading(false);
    }
  }, [dispatch, user?.uid]);
  
  /**
   * Initialize subscriptions for workout routines
   */
  const initializeSubscriptions = useCallback(() => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return;
    }
    
    subscribeToUserPublicRoutines(user.uid);
    subscribeToUserPrivateRoutines(user.uid);
    subscribeToPublicRoutines();
    subscribeToUserFavorites(user.uid);
    
    if (!wereFavRoutinesFetched) {
      getUserFavorites();
    }
  }, [
    user?.uid, 
    subscribeToUserPublicRoutines, 
    subscribeToUserPrivateRoutines, 
    subscribeToPublicRoutines, 
    subscribeToUserFavorites,
    getUserFavorites,
    wereFavRoutinesFetched
  ]);
  
  /**
   * Clear the current routine
   */
  const clearRoutine = useCallback(() => {
    dispatch(clearCurrentRoutine());
  }, [dispatch]);
  
  /**
   * Clear any errors
   */
  const clearRoutineError = useCallback(() => {
    dispatch(clearError());
    setLocalError(null);
  }, [dispatch]);
  
  /**
   * Filter routines based on search parameters
   */
  const filterRoutines = useCallback((
    routineKeys: string[],
    searchParams: WorkoutRoutineSearchParams
  ): WorkoutRoutine[] => {
    const { keyword, tag, orderByProp = 'dateCreated' } = searchParams;
    
    let filteredRoutines = routineKeys
      .map(key => routines[key])
      .filter(routine => routine !== undefined);
    
    // Filter by tag if specified
    if (tag) {
      filteredRoutines = filteredRoutines.filter(routine => 
        routine.tags && routine.tags.includes(tag)
      );
    }
    
    // Filter by keyword if specified
    if (keyword && keyword.trim() !== '') {
      const lowercaseKeyword = keyword.toLowerCase();
      filteredRoutines = filteredRoutines.filter(routine => 
        routine.title.toLowerCase().includes(lowercaseKeyword) ||
        (routine.description && routine.description.toLowerCase().includes(lowercaseKeyword))
      );
    }
    
    // Sort by specified property
    filteredRoutines.sort((a, b) => {
      if (orderByProp === 'likesCount') {
        const aLikes = a.likes ? Object.keys(a.likes).length : 0;
        const bLikes = b.likes ? Object.keys(b.likes).length : 0;
        return bLikes - aLikes; // Descending order for popularity
      } else {
        // Sort by date, newest first
        const aDate = a.dateCreated || 0;
        const bDate = b.dateCreated || 0;
        return bDate - aDate;
      }
    });
    
    return filteredRoutines;
  }, [routines]);
  
  /**
   * Check if a routine is favorited by the current user
   */
  const isRoutineFavorited = useCallback((routineKey: string): boolean => {
    return favoriteRoutines.some(favorite => favorite.routineKey === routineKey);
  }, [favoriteRoutines]);
  
  
  return {
    // State
    routines,
    userPublicRoutines,
    userPrivateRoutines,
    allPublicRoutines,
    favoriteRoutines,
    currentRoutine,
    availableTags,
    isLoading: localLoading || storeLoading,
    error: localError || storeError,
    
    // Actions
    initializeSubscriptions,
    subscribeToRoutine,
    getRoutineById,
    createNewWorkoutRoutine,
    updateExistingWorkoutRoutine,
    // Like functionality removed - use the unified useLikes hook instead
    toggleFavorite,
    uploadRoutineCoverPhoto,
    getUserFavorites,
    clearRoutine,
    clearRoutineError,
    
    // Helpers
    filterRoutines,
    isRoutineFavorited
  };
};

export default useWorkoutRoutine;